// src/components/ui/EditWorkerModal.js
import React, { useState, useEffect, useRef } from 'react';
import Modal from './Modal'; 
import { 
    User, Camera, Trash2, Clock, 
    Smartphone, MapPin, Briefcase, Calendar, Factory, 
    Fingerprint, Landmark, Hash, IndianRupee, CreditCard, CheckCircle, AlertTriangle
} from 'lucide-react';

const API_BASE = 'https://mukadampro.shirpur.store';

// --- HELPER FUNCTIONS ---
const getDateString = (date) => {
    try {
        const d = new Date(date);
        const offset = d.getTimezoneOffset() * 60000;
        return new Date(d.getTime() - offset).toISOString().split('T')[0];
    } catch(e) { return new Date().toISOString().split('T')[0]; }
};

const formatTimeForInput = (timeStr) => {
    if (!timeStr) return '';
    let cleanTime = String(timeStr).trim().replace(/^\d{4}-\d{2}-\d{2}\s+/, ''); 
    if (/^\d{2}:\d{2}$/.test(cleanTime)) return cleanTime; 
    
    const isPM = cleanTime.toUpperCase().includes('PM');
    const isAM = cleanTime.toUpperCase().includes('AM');
    cleanTime = cleanTime.replace(/AM|PM/i, '').trim();
    let parts = cleanTime.split(':');
    if (parts.length < 2) return '';
    
    let h = parseInt(parts[0], 10);
    let m = parseInt(parts[1], 10);
    if (isNaN(h) || isNaN(m)) return '';
    
    if (isPM && h !== 12) h += 12;
    if (isAM && h === 12) h = 0;
    
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const EditWorkerModal = ({ 
    isOpen, 
    onClose, 
    onSave, 
    workerData, 
    availableLooms = [], 
    handleVoiceClick, 
    handleFocusVoice, 
    handleTextBlurVoice 
}) => {
  const [data, setData] = useState({ 
      id: null, name: '', mobile: '', address: '', 
      shift: 'Day', looms: [], rate_per_meter: 1.25, 
      bank_account: '', ifsc: '', bank_name: '', upi_id: '', aadhaar: '', 
      doj: getDateString(new Date()), 
      profile_pic: null,
      shift_start: '08:00', shift_end: '20:00', 
      is_badli: 0 
  });
  
  const [unavailableLooms, setUnavailableLooms] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPhotoMenu, setShowPhotoMenu] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const fileInputRef = useRef(null);
  const containerRef = useRef(null); // 🔥 Added Ref for Scroll Fix
  const isEditMode = Boolean(workerData && workerData.id);

  // 🔥 FIX FOR FLEXBOX SCROLL CUT-OFF (Top hidden issue)
  useEffect(() => {
    if (isOpen && containerRef.current && containerRef.current.parentElement) {
        containerRef.current.parentElement.style.justifyContent = 'flex-start';
        containerRef.current.parentElement.style.paddingTop = '20px';
        containerRef.current.parentElement.style.paddingBottom = '20px';
    }
  }, [isOpen]);

  useEffect(() => { 
      if (isOpen) {
          if (workerData) {
              let loomArray = [];
              if (workerData.looms) {
                  loomArray = workerData.looms.toString().split(',').map(s => s.replace(/[^0-9]/g, '').trim()).filter(s => s !== '');
              }
              setData({ 
                  ...workerData, 
                  looms: loomArray, 
                  rate_per_meter: workerData.rate_per_meter || workerData.ratePerMeter || 1.25,
                  doj: workerData.doj ? getDateString(workerData.doj) : getDateString(new Date()),
                  shift_start: formatTimeForInput(workerData.shift_start) || '08:00',
                  shift_end: formatTimeForInput(workerData.shift_end) || '20:00'
              });
              if(workerData.profile_image) setPreviewUrl(workerData.profile_image);
              else setPreviewUrl(null);
          } else {
              setData({ id: null, name: '', mobile: '', address: '', shift: 'Day', looms: [], rate_per_meter: 1.25, bank_account: '', ifsc: '', bank_name: '', upi_id: '', aadhaar: '', doj: getDateString(new Date()), profile_pic: null, shift_start: '08:00', shift_end: '20:00', is_badli: 0 });
              setPreviewUrl(null);
          }
          setShowDeletePopup(false);
          setShowPhotoMenu(false);
      }
  }, [workerData, isOpen]);

  useEffect(() => {
    if(isOpen && data.shift_start && data.shift_end) {
        const s = data.shift_start;
        const e = data.shift_end;
        fetch(`${API_BASE}/get_occupied_looms.php?start=${s}&end=${e}&exclude_id=${data.id || 0}`)
            .then(res => res.json())
            .then(json => { if(json.status === 'success') setUnavailableLooms(json.occupied); })
            .catch(err => console.error("Loom fetch failed:", err));
    }
  }, [data.shift_start, data.shift_end, data.id, isOpen]);

  const handleLoomToggle = (loomId) => {
      const idStr = String(loomId);
      if (unavailableLooms.includes(loomId)) return;
      let newLooms = [...data.looms];
      if (newLooms.includes(idStr)) newLooms = newLooms.filter(l => l !== idStr);
      else newLooms.push(idStr);
      setData({ ...data, looms: newLooms });
  };
  
  const handleFileChange = (e) => { 
    const file = e.target.files[0]; 
    if (file) { 
        setData({ ...data, profile_pic: file }); 
        setPreviewUrl(URL.createObjectURL(file)); 
    } 
    setShowPhotoMenu(false);
  };
  
  const handleRemovePhoto = () => {
      setPreviewUrl(null);
      setData({ ...data, profile_pic: null });
      setShowPhotoMenu(false);
  };

  const confirmAndDelete = async () => { 
      try {
        await fetch(`${API_BASE}/delete_worker.php`, { method: 'POST', body: JSON.stringify({ id: data.id }) }); 
        onSave(); 
      } catch(e) { alert("Delete failed"); }
  };
  
  const handleSave = async (e) => { 
    e.preventDefault(); 
    
    if (data.looms.length === 0) {
        alert("Please assign at least one loom.");
        return;
    }

    if(data.shift_start && data.shift_end) {
        const getMins = (t) => { const [h,m] = t.split(':').map(Number); return h*60+m; };
        const sMins = getMins(data.shift_start); 
        const eMins = getMins(data.shift_end);
        
        if (sMins === eMins) {
            alert("Error: Duty Start and End time cannot be exactly the same.");
            return;
        }

        let duration = eMins - sMins;
        if (duration < 0) duration += (24 * 60);

        if (duration > 720) {
            alert("Error: Shift overlapping detected! Duty duration cannot exceed 12 hours (720 minutes). Please fix the start and end times.");
            return;
        }

        if(data.shift === 'Day') {
            if (sMins >= 18*60 || eMins <= 6*60) { 
                alert("Overlap Error: Day shift timings are conflicting with Night boundaries. Please adjust."); return; 
            }
        } else {
            const startsInDay = (sMins >= 6*60 && sMins < 17*60);
            const endsInDay = (eMins > 9*60 && eMins < 18*60);
            if (startsInDay || endsInDay) { 
                alert("Overlap Error: Night shift timings are bleeding into active Day hours. Please adjust."); return; 
            }
        }
    } else {
        alert("Please set both Shift Start and Shift End times.");
        return;
    }
    
    setIsSaving(true);
    let cleanLoomString = '';
    if (Array.isArray(data.looms) && data.looms.length > 0) {
        const uniqueNums = [...new Set(data.looms.map(l => parseInt(l)).filter(n => !isNaN(n)))];
        uniqueNums.sort((a, b) => a - b);
        cleanLoomString = uniqueNums.join(',');
    }

    const formData = new FormData();
    Object.keys(data).forEach(key => {
        if(key === 'profile_pic' && data[key]) formData.append('profile_pic', data[key]);
        else if (key === 'looms') formData.append('looms', cleanLoomString);
        else formData.append(key, data[key]);
    });

    try {
      const endpoint = data.id ? `${API_BASE}/update_worker.php` : `${API_BASE}/save_worker.php`;
      const res = await fetch(endpoint, { method: 'POST', body: formData });
      const text = await res.text();
      try {
          const json = JSON.parse(text);
          if(json.status === 'success') { 
              if(handleVoiceClick) handleVoiceClick(e, "वर्कर का डाटा सेव कर लिया गया है।", null);
              onSave(); 
          } else { alert(json.message); }
      } catch (e) { console.error("Server Error:", text); alert("Server Error"); }
    } catch (err) { alert("Network Error"); }
    finally { setIsSaving(false); }
  };

  const executeFocusVoice = (msg) => {
      if(window.speechSynthesis) window.speechSynthesis.cancel();
      setTimeout(() => { if (handleFocusVoice) handleFocusVoice(msg); }, 150);
  };

  const executeBlurVoice = (e, value, fieldName) => {
      if (e.relatedTarget) return;
      if (handleTextBlurVoice) handleTextBlurVoice(value, fieldName);
  };

  return (
    <Modal 
        isOpen={isOpen} 
        onClose={(e) => {
            if (window.speechSynthesis) window.speechSynthesis.cancel();
            onClose(e);
        }} 
        title={isEditMode ? "EDIT WORKER PROFILE" : "ADD NEW WORKER"} 
        icon={User} 
        iconColor="#0F172A" 
        iconBg="#F8FAFC"
        bg="#FFFFFF" 
        maxWidth="420px"
    >
        <style>{`
            ::-webkit-scrollbar { width: 4px; }
            ::-webkit-scrollbar-track { background: transparent; }
            ::-webkit-scrollbar-thumb { background: #E2E8F0; border-radius: 10px; }
        `}</style>
        
        <form 
            ref={containerRef} 
            onSubmit={handleSave} 
            style={{ 
                marginTop: '0px', 
                zoom: 0.95, 
                width: '100%', 
                boxSizing: 'border-box',
                maxHeight: '75vh', // 🔥 Internal scroll container
                overflowY: 'auto', // 🔥 Enables smooth scrolling inside
                overflowX: 'hidden',
                padding: '2px', // Prevent shadows from being cut off
                paddingRight: '6px' // Space for scrollbar
            }}
        >
            
           {/* 1. PREMIUM PROFILE HEADER (Photo on Left, Details on Right) */}
            <div style={styles.premiumCard}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                    
                    {/* LEFT SIDE: PHOTO UPLOAD */}
                    <div style={{ position: 'relative', flexShrink: 0, marginTop: '10px' }}>
                        <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#F8FAFC', border: '2px dashed #CBD5E1', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', boxShadow: 'inset 0 2px 4px rgba(15,23,42,0.02)' }}>
                            {previewUrl ? <img src={previewUrl} alt="Preview" style={{width:'100%', height:'100%', objectFit:'cover'}} /> : <User size={28} color="#94A3B8"/>}
                        </div>
                        <button type="button" onClick={() => setShowPhotoMenu(!showPhotoMenu)} style={{ position: 'absolute', bottom: '-4px', right: '-4px', background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}>
                            <Camera size={14} color="#0F172A" />
                        </button>
                        
                        <input type="file" accept="image/*" ref={fileInputRef} onChange={handleFileChange} style={{display:'none'}} />
                        
                        {showPhotoMenu && (
                            <div style={{ position: 'absolute', top: '90px', left: '0', background: '#FFFFFF', borderRadius: '12px', padding: '6px', width: '160px', zIndex: 100, border: '1px solid #E2E8F0', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}>
                                {!previewUrl && (
                                   <button type="button" onClick={() => { setTimeout(() => { fileInputRef.current.click(); }, 150); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', background: 'transparent', border: 'none', padding: '10px', fontSize: '11px', fontWeight: '700', color: '#0F172A', cursor: 'pointer', textAlign: 'left' }}>
                                        <Camera size={14} color="#0F172A"/> Add Photo
                                   </button>
                                )}
                                {previewUrl && (
                                    <>
                                        <button type="button" onClick={() => { setTimeout(() => { fileInputRef.current.click(); }, 150); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', background: 'transparent', border: 'none', padding: '10px', fontSize: '11px', fontWeight: '700', color: '#0F172A', cursor: 'pointer', textAlign: 'left' }}>
                                            <Camera size={14} color="#0F172A"/> Change Photo
                                        </button>
                                        <div style={{height:'1px', background:'#F1F5F9', margin:'4px 0'}}></div>
                                        <button type="button" onClick={handleRemovePhoto} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', background: 'transparent', border: 'none', padding: '10px', fontSize: '11px', fontWeight: '700', color: '#EF4444', cursor: 'pointer', textAlign: 'left' }}>
                                            <Trash2 size={14} color="#EF4444"/> Remove Photo
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    {/* RIGHT SIDE: PERSONAL DETAILS STACK */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {isEditMode && (
                            <div>
                                <label style={{...styles.label, marginBottom: '4px'}}><Hash size={12} color="#64748B" /> WORKER ID</label>
                                <div style={{...styles.input, padding: '8px 10px', background: '#F1F5F9', color: '#94A3B8', border: '1px dashed #CBD5E1', cursor: 'not-allowed', display: 'flex', alignItems: 'center'}}>
                                    #{data.id}
                                </div>
                            </div>
                        )}
                        <div>
                            <label style={{...styles.label, marginBottom: '4px'}}><User size={12} color="#64748B" /> FULL NAME <span style={{color:'#EF4444'}}>*</span></label>
                            <input style={{...styles.input, padding: '8px 10px'}} value={data.name} onChange={e=>setData({...data,name:e.target.value})} onFocus={() => executeFocusVoice(" वर्कर का नाम डालें।")} onBlur={(e) => executeBlurVoice(e, e.target.value, "नाम")} placeholder="Enter full name" required />
                        </div>
                        <div>
                            <label style={{...styles.label, marginBottom: '4px'}}><Smartphone size={12} color="#64748B" /> MOBILE <span style={{color:'#EF4444'}}>*</span></label>
                            <input type="number" style={{...styles.input, padding: '8px 10px'}} value={data.mobile} onChange={e=>setData({...data,mobile:e.target.value})} onFocus={() => executeFocusVoice(" मोबाइल नंबर डालें।")} onBlur={(e) => executeBlurVoice(e, e.target.value, "मोबाइल")} placeholder="10-digit" required />
                        </div>
                        <div>
                            <label style={{...styles.label, marginBottom: '4px'}}><MapPin size={12} color="#64748B" /> ADDRESS <span style={{color:'#EF4444'}}>*</span></label>
                            <input style={{...styles.input, padding: '8px 10px'}} value={data.address} onChange={e=>setData({...data,address:e.target.value})} placeholder="Enter full address" required />
                        </div>
                        <div>
                            <label style={{...styles.label, marginBottom: '4px'}}><Calendar size={12} color="#64748B" /> JOINING DATE</label>
                            <input type="date" style={{...styles.input, padding: '8px 10px', background: '#F1F5F9', color: '#94A3B8', cursor: 'not-allowed', border: '1px dashed #CBD5E1'}} value={data.doj} readOnly title="Joining date cannot be changed" />
                        </div>
                    </div>
                </div>
            </div>

            {/* 2. WORK DETAILS CARD (Shift, Rate, Duty Time) */}
            <div style={styles.premiumCard}>
                <div style={styles.row}>
                    <div>
                        <label style={styles.label}><Briefcase size={12} color="#64748B" /> SHIFT <span style={{color:'#EF4444'}}>*</span></label>
                        <select style={{...styles.input, cursor:'pointer'}} value={data.shift} onChange={e=>setData({...data,shift:e.target.value})} required>
                            <option value="Day">Day</option>
                            <option value="Night">Night</option>
                        </select>
                    </div>
                    <div>
                        <label style={styles.label}><IndianRupee size={12} color="#64748B" /> RATE (₹) <span style={{color:'#EF4444'}}>*</span></label>
                        <input type="number" step="0.01" style={styles.input} value={data.rate_per_meter} onChange={e=>setData({...data,rate_per_meter:e.target.value})} onFocus={() => executeFocusVoice(" रेट सेट करें।")} required />
                    </div>
                </div>
                
                {/* 🔥 Premium Combined Start and End Time Box */}
                <div style={{ marginBottom: '10px' }}>
                    <label style={styles.label}><Clock size={12} color="#64748B" /> DUTY START / END TIME <span style={{color:'#EF4444'}}>*</span></label>
                    
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        border: '1px solid rgba(226, 232, 240, 0.8)', 
                        borderRadius: '14px', 
                        background: '#FFFFFF', 
                        padding: '6px', 
                        boxShadow: '0 4px 15px -3px rgba(15, 23, 42, 0.05)' 
                    }}>
                        
                        {/* Start Time Inner Box */}
                        <div style={{ flex: 1, background: '#F8FAFC', borderRadius: '10px', padding: '8px 10px', display: 'flex', alignItems: 'center', border: '1px solid #F1F5F9', boxShadow: 'inset 0 1px 2px rgba(15,23,42,0.01)' }}>
                            <input 
                                style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', fontSize: '12.5px', color: '#0F172A', fontWeight: '800', cursor: 'pointer', letterSpacing: '0.5px' }} 
                                type="time" 
                                value={data.shift_start} 
                                onChange={e=>setData({...data,shift_start:e.target.value})} 
                                onFocus={() => executeFocusVoice(" ड्यूटी शुरू होने का समय सेट करें।")} 
                                required 
                            />
                        </div>
                        
                        {/* Premium 'TO' Badge */}
                        <div style={{ padding: '0 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ fontSize: '8.5px', color: '#64748B', fontWeight: '900', background: '#F1F5F9', padding: '4px 8px', borderRadius: '8px', letterSpacing: '1px', border: '1px solid #E2E8F0', boxShadow: '0 1px 2px rgba(15,23,42,0.02)' }}>
                                TO
                            </span>
                        </div>
                        
                        {/* End Time Inner Box */}
                        <div style={{ flex: 1, background: '#F8FAFC', borderRadius: '10px', padding: '8px 10px', display: 'flex', alignItems: 'center', border: '1px solid #F1F5F9', boxShadow: 'inset 0 1px 2px rgba(15,23,42,0.01)' }}>
                            <input 
                                style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', fontSize: '12.5px', color: '#0F172A', fontWeight: '800', cursor: 'pointer', textAlign: 'right', letterSpacing: '0.5px' }} 
                                type="time" 
                                value={data.shift_end} 
                                onChange={e=>setData({...data,shift_end:e.target.value})} 
                                onFocus={() => executeFocusVoice(" ड्यूटी खत्म होने का समय सेट करें।")} 
                                required 
                            />
                        </div>
                    </div>
                </div>
                
                <div style={{fontSize:'8.5px', color: '#94A3B8', fontWeight:'700', textTransform: 'uppercase'}}>Max Duty limit: 12 Hours (Anti-Clash Protected)</div>
            </div>

          
            {/* 3. ASSIGN LOOMS CARD */}
            <div style={styles.premiumCard}>
                <label style={styles.label}><Factory size={12} color="#64748B" /> ASSIGN LOOMS <span style={{color:'#EF4444'}}>*</span></label>
                <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', maxHeight:'180px', overflowY:'auto' }}>
                    {availableLooms && availableLooms.length > 0 ? availableLooms.map(l => {
                        const isSelected = data.looms.includes(String(l.loom_id));
                        const isUnavailable = unavailableLooms.includes(l.loom_id); 
                        return (
                            <span 
                                key={l.loom_id} onClick={() => handleLoomToggle(l.loom_id)} 
                                style={{
                                    fontSize:'10.5px', padding:'6px 12px', borderRadius:'8px', cursor: isUnavailable ? 'not-allowed' : 'pointer', fontWeight:'700',
                                    background: isSelected ? '#ECFDF5' : '#F8FAFC',
                                    color: isSelected ? '#059669' : (isUnavailable ? '#94A3B8' : '#64748B'),
                                    border: isSelected ? '1px solid #10B981' : (isUnavailable ? '1px dashed #CBD5E1' : '1px solid #E2E8F0'),
                                    transition: 'all 0.2s ease',
                                    boxShadow: isSelected ? '0 2px 4px rgba(16, 185, 129, 0.1)' : 'none',
                                    opacity: isUnavailable ? 0.5 : 1,
                                }}
                            >
                                {l.loom_id}
                            </span>
                        )
                    }) : <span style={{fontSize:'9px', color:'#94A3B8', fontWeight:'600', textTransform: 'uppercase'}}>No looms available</span>}
                </div>
            </div>

            {/* 4. OPTIONAL: BANK & ID DETAILS CARD */}
            <div style={styles.premiumCard}>
                <label style={styles.label}><Landmark size={12} color="#64748B" /> OPTIONAL: BANK & ID DETAILS</label>
                <div style={{ marginBottom: '10px', marginTop: '10px' }}>
                    <label style={styles.label}><Fingerprint size={12} color="#64748B" /> AADHAAR NUMBER</label>
                    <input type="number" style={styles.input} value={data.aadhaar} onChange={e=>setData({...data,aadhaar:e.target.value})} placeholder="12-digit Aadhaar" />
                </div>
                <div style={styles.row}>
                    <div>
                        <label style={styles.label}><Landmark size={12} color="#64748B" /> BANK NAME</label>
                        <input style={styles.input} value={data.bank_name} onChange={e=>setData({...data,bank_name:e.target.value})} placeholder="Bank Name" />
                    </div>
                    <div>
                        <label style={styles.label}><Hash size={12} color="#64748B" /> IFSC CODE</label>
                        <input style={styles.input} value={data.ifsc} onChange={e=>setData({...data,ifsc:e.target.value})} placeholder="IFSC" />
                    </div>
                </div>
                <div style={styles.row}>
                    <div>
                        <label style={styles.label}><CreditCard size={12} color="#64748B" /> ACCOUNT NO.</label>
                        <input type="number" style={styles.input} value={data.bank_account} onChange={e=>setData({...data,bank_account:e.target.value})} placeholder="Acc No." />
                    </div>
                    <div>
                        <label style={styles.label}><CreditCard size={12} color="#64748B" /> UPI ID</label>
                        <input style={styles.input} value={data.upi_id} onChange={e=>setData({...data,upi_id:e.target.value})} placeholder="e.g. xxx@ybl" />
                    </div>
                </div>
            </div>

            {/* 🌟 GREEN EXECUTE MODULE 🌟 */}
            <div style={{
                background: 'linear-gradient(145deg, #FFFFFF, #F8FAFC)',
                borderRadius: '16px',
                padding: '12px',
                boxShadow: '0 8px 25px -5px rgba(15, 23, 42, 0.05)',
                border: '1px solid rgba(226, 232, 240, 0.8)',
                marginBottom: '10px',
                marginTop: '16px',
                position: 'sticky', // 🔥 Ensures it sticks at the bottom while scrolling!
                bottom: '0',
                zIndex: 10
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ background: '#ECFDF5', padding: '8px', borderRadius: '10px', border: '1px solid #A7F3D0' }}>
                            <User size={16} color="#10B981" strokeWidth={2.5} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                            <span style={{ fontSize: '12px', fontWeight: '800', color: '#0F172A', letterSpacing: '-0.2px', textTransform: 'uppercase', lineHeight: '1.2' }}>
                                {data.name ? data.name : 'NEW WORKER'}
                            </span>
                            <span style={{ fontSize: '9px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                {isEditMode ? 'EDIT PROFILE' : 'CREATE PROFILE'}
                            </span>
                        </div>
                    </div>
                    
                    {isEditMode && (
                        <button type="button" onClick={() => setShowDeletePopup(true)} style={styles.deleteBtn}>
                            <Trash2 size={14} />
                        </button>
                    )}
                </div>

                <button type="submit" disabled={isSaving} style={styles.premiumSubmitBtn}>
                    <CheckCircle size={16} color="#FFFFFF" strokeWidth={2.5} />
                    <span>{isSaving ? 'SAVING...' : (isEditMode ? 'UPDATE WORKER PROFILE' : 'EXECUTE WORKER CREATION')}</span>
                </button>
            </div>

        </form>

        {/* INNER DELETE POPUP */}
        {showDeletePopup && (
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999999, padding: '20px' }}>
                <div style={{ background: '#ffffff', borderRadius: '24px', padding: '24px', textAlign: 'center', width: '90%', maxWidth: '300px', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', animation: 'zoomIn 0.2s' }}>
                    <div style={{ background: '#FEE2E2', width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px auto' }}>
                        <AlertTriangle size={24} color="#DC2626"/>
                    </div>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '800', color: '#0F172A', letterSpacing: '-0.3px' }}>Delete Worker?</h3>
                    <p style={{ margin: '0 0 20px 0', fontSize: '12px', fontWeight: '600', color: '#64748B' }}>This will permanently remove the worker's profile and unassign all looms.</p>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button type="button" onClick={() => setShowDeletePopup(false)} style={{ flex: 1, padding: '12px', background: '#F8FAFC', color: '#475569', border: '1px solid #E2E8F0', borderRadius: '12px', fontWeight: '700', cursor: 'pointer' }}>Cancel</button>
                        <button type="button" onClick={confirmAndDelete} style={{ flex: 1, padding: '12px', background: '#DC2626', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '700', cursor: 'pointer' }}>Delete</button>
                    </div>
                </div>
            </div>
        )}
    </Modal>
  );
};

// --- STYLES ---
const styles = {
    premiumCard: { 
        background: '#FFFFFF', 
        padding: '12px',
        borderRadius: '16px',
        border: '1px solid rgba(226, 232, 240, 0.8)', 
        boxShadow: '0 4px 15px -3px rgba(15, 23, 42, 0.03)', 
        marginBottom: '12px',
        position: 'relative',
        overflow: 'hidden'
    },
    label: { 
        fontSize: '8.5px',
        fontWeight: '800', 
        color: '#64748B', 
        display: 'flex', 
        marginBottom: '6px',
        textTransform:'uppercase', 
        letterSpacing: '0.6px',
        alignItems: 'center',
        gap: '4px'
    },
    input: { 
        width: '100%', 
        padding: '10px 12px',
        borderRadius: '10px', 
        border: '1px solid #E2E8F0', 
        fontSize: '11px', 
        outline: 'none', 
        background: '#F8FAFC', 
        color:'#0F172A', 
        fontWeight:'700', 
        boxSizing:'border-box', 
        transition: 'all 0.3s ease', 
        boxShadow: 'inset 0 1px 3px rgba(15,23,42,0.01)'
    },
    row: { 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: '10px', 
        marginBottom: '10px' 
    },
    premiumSubmitBtn: {
        width: '100%', 
        padding: '12px',
        background: 'linear-gradient(135deg, #10B981, #059669)',
        color: '#FFFFFF', 
        border: 'none', 
        borderRadius: '12px', 
        fontWeight: '900', 
        cursor: 'pointer', 
        fontSize:'11.5px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '8px', 
        boxShadow: '0 6px 16px rgba(16, 185, 129, 0.25)',
        textTransform: 'uppercase',
        letterSpacing: '0.8px',
        transition: 'all 0.3s ease'
    },
    deleteBtn: { 
        width: '32px', 
        height: '32px', 
        flexShrink: 0, 
        background: '#FEF2F2', 
        color: '#DC2626', 
        border: '1px solid #6e5252ff', 
        borderRadius: '10px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        cursor: 'pointer', 
        boxShadow: '0 4px 10px rgba(220, 38, 38, 0.1)' 
    }
};

export default EditWorkerModal;